import PaidBreakInputs from "./PaidBreakInputs";
import { requiredRestBreakCount, restBreakDurationHours } from "./restBreaks";
import { normalizePaidBreaks, validatePaidBreaks } from "./paidBreakValidation";
import { getDefaultPaidBreakStart } from "../../../../utilities/defaultPaidBreakStart";
import { text_to_num_minute_precision } from "../../../../helpers/helpers";
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Drawer from "@mui/material/Drawer";
import AddTaskIcon from "@mui/icons-material/AddTask";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShiftInputsSlider from "./ShiftInputsSlider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import PersonAvatar from "./PersonAvatar";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import SaveIcon from "@mui/icons-material/Save";
import LoadingButton from "@mui/lab/LoadingButton";
import ShiftCardModalDisplay from "./ShiftCardModalDisplay";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { ThemeProvider } from "@mui/material/styles";
import { createRightworkTheme } from "../../../../theme/rightworkTheme";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Tooltip from "@mui/material/Tooltip";
import {
  checkIfValid,
  text_to_num,
  createDayArray,
  generateTimeOptions,
  get_t_str,
  filterOptions,
} from "../../../../helpers/helpers";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../../requests/shared/helpers/DrawerFooterContainerSmall";
import Autocomplete from "@mui/material/Autocomplete";
import {
  buildOpenShiftSavePayload,
  getAssignableOpenShiftEmployees,
  getOpenShiftEmployeeUserId,
  normalizeOpenShiftUserId,
} from "./openShiftAssignment";

// Modal style object
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

const theme = createRightworkTheme({
  status: {
    danger: "#e53e3e",
  },
  palette: {
    neutral: {
      main: "#64748B",
      contrastText: "#fff",
    },
  },
});

const OPTION_SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#6d28d9",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#6d28d9",
  },
};
const OPTION_SWITCH_LABEL_SX = { marginLeft: "-6px", marginRight: 0 };

function OpenShiftInputsModal(props) {
  const shiftQualifiesForSecondBreak = React.useCallback(
    (shiftLength) => {
      const threshold = props.secondBreakShiftLengthThreshold;

      if (!threshold || threshold.hours == null) {
        return false;
      }

      return threshold.inclusive
        ? shiftLength >= threshold.hours
        : shiftLength > threshold.hours;
    },
    [props.secondBreakShiftLengthThreshold]
  );

  const initialShiftInfoString = {
    shift_start: get_t_str(props.shift_info.shift_start),
    shift_end: get_t_str(props.shift_info.shift_end),
  };
  const initialBreakInfoString = {
    break_start: get_t_str(props.shift_info.break_start),
    break_end: get_t_str(
      props.shift_info.break_start + props.shift_info.break_length
    ),
  };
  const initialBreakInfoString2 = {
    break_start: get_t_str(props.shift_info.break_start_2 || 0),
    break_end: get_t_str(
      (props.shift_info.break_start_2 || 0) +
        (props.shift_info.break_length_2 || 0)
    ),
  };
  const initialBreakChecked = props.shift_info.break_length > 0;
  const initialBreakChecked2 = (props.shift_info.break_length_2 || 0) > 0;

  const [state, setState] = React.useState(props.shift_info);
  const [paidBreaksChecked, setPaidBreaksChecked] = React.useState(
    Boolean(props.shift_info.paid_breaks?.length)
  );
  const [isPaidBreakManuallyControlled, setIsPaidBreakManuallyControlled] = React.useState(false);
  const paidBreakDurationHours = restBreakDurationHours(props.restBreakRegulation);
  const paidBreaks = normalizePaidBreaks(state.paid_breaks);
  const paidBreakValidation = validatePaidBreaks(state, Boolean(props.paidBreakInputsEnabled));
  const canAddPaidBreak =
    getDefaultPaidBreakStart(state, paidBreaks, paidBreakDurationHours) !== null;

  const updatePaidBreaks = (breaks) => {
    setIsPaidBreakManuallyControlled(true);
    setState((previous) => ({ ...previous, paid_breaks: normalizePaidBreaks(breaks) }));
  };
  const handleAddPaidBreak = () => {
    const breakStart = getDefaultPaidBreakStart(state, paidBreaks, paidBreakDurationHours);
    if (breakStart === null) return;
    updatePaidBreaks([...paidBreaks, {
      break_start: breakStart,
      break_duration: paidBreakDurationHours,
    }]);
  };
  const handlePaidBreaksCheckChange = (event) => {
    setIsPaidBreakManuallyControlled(true);
    const checked = event.target.checked;
    setPaidBreaksChecked(checked);
    if (!checked) updatePaidBreaks([]);
    else if (paidBreaks.length === 0) handleAddPaidBreak();
  };
  const handlePaidBreakChange = (index, value) => {
    updatePaidBreaks(paidBreaks.map((entry, entryIndex) => entryIndex === index
      ? { ...entry, break_start: text_to_num_minute_precision(value) }
      : entry));
  };
  const handleRemovePaidBreak = (index) => {
    updatePaidBreaks(paidBreaks.filter((_, entryIndex) => entryIndex !== index));
  };

  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState(null);

  const [break_checked, set_break_checked] =
    React.useState(initialBreakChecked);
  const [break_checked_2, set_break_checked_2] =
    React.useState(initialBreakChecked2);
  const [isBreakManuallyControlled, setIsBreakManuallyControlled] =
    React.useState(false);

  const [savingLoading, setSavingLoading] = React.useState(false);
  const [deletingLoading, setDeletingLoading] = React.useState(false);

  const [shiftInfoString, setShiftInfoString] = React.useState(
    initialShiftInfoString
  );
  const [breakInfoString, setBreakInfoString] = React.useState(
    initialBreakInfoString
  );
  const [breakInfoString2, setBreakInfoString2] = React.useState(
    initialBreakInfoString2
  );

  const [appliedTo, setAppliedTo] = React.useState([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  React.useEffect(() => {
    setState(props.shift_info);
    setPaidBreaksChecked(Boolean(props.shift_info.paid_breaks?.length));
    setIsPaidBreakManuallyControlled(false);
    setSelectedEmployeeId(null);
    setShiftInfoString(initialShiftInfoString);
    setBreakInfoString(initialBreakInfoString);
    setBreakInfoString2(initialBreakInfoString2);
    set_break_checked(initialBreakChecked);
    set_break_checked_2(initialBreakChecked2);

    let appliedToCopy = [false, false, false, false, false, false, false];
    appliedToCopy[props.d] = true;
    setAppliedTo(appliedToCopy);
  }, [props.shift_info]);

  const assignableEmployees = React.useMemo(
    () =>
      getAssignableOpenShiftEmployees(
        props.store_team_member_weekly_info,
        state.role_id,
        state.pending_pickup_user_ids
      ),
    [
      props.store_team_member_weekly_info,
      state.pending_pickup_user_ids,
      state.role_id,
    ]
  );
  const pendingPickupUserIds = React.useMemo(
    () =>
      new Set(
        (state.pending_pickup_user_ids || []).map(normalizeOpenShiftUserId)
      ),
    [state.pending_pickup_user_ids]
  );
  const selectedEmployee =
    assignableEmployees.find(
      (employee) => employee.id === selectedEmployeeId
    ) || null;

  React.useEffect(() => {
    if (selectedEmployeeId !== null && !selectedEmployee) {
      setSelectedEmployeeId(null);
    }
  }, [selectedEmployee, selectedEmployeeId]);

  // Auto-update breakChecked based on shift length
  React.useEffect(() => {
    if (!isBreakManuallyControlled && !props.existingShift) {
      const shiftLength = state.shift_end - state.shift_start;

      const breakRequired =
        props.breakRequiredWhenShiftLengthExceeds !== null
          ? shiftLength > props.breakRequiredWhenShiftLengthExceeds
          : false;

      set_break_checked(breakRequired || initialBreakChecked);

      if (breakRequired && !initialBreakChecked) {
        let new_break_length = props.defaultBreakLengthHours;
        let new_break_start =
          Math.round(((state.shift_end + state.shift_start) / 2) * 4) / 4 -
          new_break_length / 2;

        const updatedShift = {
          ...state,
          break_start: new_break_start,
          break_end: new_break_start + new_break_length,
          break_length: new_break_length,
        };

        setState(updatedShift);
        setBreakInfoString({
          break_start: get_t_str(new_break_start),
          break_end: get_t_str(new_break_start + new_break_length),
        });
      }

      setShiftInfoString({
        shift_start: get_t_str(state.shift_start),
        shift_end: get_t_str(state.shift_end),
      });
    }
  }, [
    state.shift_start,
    state.shift_end,
    props.breakRequiredWhenShiftLengthExceeds,
  ]);

  const shiftLength = state.shift_end - state.shift_start;
  const shiftQualifiesForConfiguredSecondBreak =
    shiftQualifiesForSecondBreak(shiftLength);
  const showSecondBreakOption =
    shiftQualifiesForConfiguredSecondBreak ||
    break_checked_2 ||
    (state.break_length_2 || 0) > 0;
  const showBusinessDeclineOption = props.businessDeclineShiftOptionEnabled;
  const showBreakOption = true;
  const showNoteOption = true;
  const hasOptionAfterBusinessDecline = showBreakOption || showNoteOption;
  const hasOptionAfterBreak = !props.paidBreakInputsEnabled && showNoteOption;

  React.useEffect(() => {
    if (props.existingShift || shiftQualifiesForConfiguredSecondBreak) {
      return;
    }

    if (!break_checked_2 && (state.break_length_2 || 0) <= 0) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      break_start_2: 0,
      break_length_2: 0,
    }));
    setBreakInfoString2({
      break_start: get_t_str(0),
      break_end: get_t_str(0),
    });
    set_break_checked_2(false);
  }, [
    break_checked_2,
    props.existingShift,
    shiftQualifiesForConfiguredSecondBreak,
    state.break_length_2,
  ]);

  function handleAppliedToChange(day_i) {
    let appliedToCopy = [...appliedTo];
    if (day_i !== props.d) {
      appliedToCopy[day_i] = !appliedTo[day_i];
    }
    // console.log(appliedToCopy);
    setAppliedTo(appliedToCopy);
  }

  function appliedToButtonGroup() {
    return (
      <div className="w-full mt-2 bg-slate-50 rounded-lg flex">
        <ThemeProvider theme={theme}>
          <ButtonGroup
            style={{
              flex: 1,
              border: "1px solid #f1f5f9",
              borderRadius: "7px",
            }}
          >
            {appliedTo.map((d, i) => (
              <Button
                fullWidth
                color="neutral"
                size="small"
                style={{
                  border: d ? "1px solid #6d28d9" : "1px solid #f8fafc",
                  borderRadius: "7px",
                  backgroundColor: d ? "#ede9fe" : "transparent",
                  padding: "6px",
                  margin: "0 0.5px",
                }}
                key={i}
                onClick={() => handleAppliedToChange(i)}
              >
                {day_abbrevs[i]}
              </Button>
            ))}
          </ButtonGroup>
        </ThemeProvider>
      </div>
    );
  }

  function handleChange(evt) {
    const { name, value } = evt.target;
    setState({
      ...state,
      [name]: value,
      role_type: getRoleType(value, props.role_names),
    });
  }

  function handleShiftInfo(evt) {
    // console.log(state);
    // console.log(props.shift_info);
    const value = evt.target.value;
    setShiftInfoString({
      ...shiftInfoString,
      [evt.target.name]: value,
    });
    // console.log(text_to_num(value));
    setState({
      ...state,
      [evt.target.name]: text_to_num(value),
    });
  }

  function handleBreakInfo(evt) {
    const value = evt.target.value;
    if (evt.target.name === "break_start" || evt.target.name === "break_end") {
      setBreakInfoString({
        ...breakInfoString,
        [evt.target.name]: value,
      });
      return;
    }

    const breakInfoKey =
      evt.target.name === "break_start_2"
        ? "break_start"
        : evt.target.name === "break_end_2"
          ? "break_end"
          : evt.target.name;

    setBreakInfoString2({
      ...breakInfoString2,
      [breakInfoKey]: value,
    });
  }

  React.useEffect(() => {
    setState({
      ...state,
      ["break_start"]: text_to_num(breakInfoString["break_start"]),
      ["break_length"]:
        text_to_num(breakInfoString["break_end"]) -
        text_to_num(breakInfoString["break_start"]),
    });
  }, [breakInfoString]);

  React.useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      ["break_start_2"]: text_to_num(breakInfoString2["break_start"]),
      ["break_length_2"]:
        text_to_num(breakInfoString2["break_end"]) -
        text_to_num(breakInfoString2["break_start"]),
    }));
  }, [breakInfoString2]);

  function handleCheckChange(e) {
    const { checked } = e.target;
    set_break_checked(checked);

    if (checked) {
      let new_break_length =
        state.shift_end - state.shift_start > 6
          ? 1
          : props.defaultBreakLengthHours;
      let new_break_start =
        Math.round(((state.shift_end + state.shift_start) / 2) * 4) / 4 -
        new_break_length / 2;

      setState({
        ...state,
        break_start: new_break_start,
        break_length: new_break_length,
      });
      setBreakInfoString({
        break_start: get_t_str(new_break_start),
        break_end: get_t_str(new_break_start + new_break_length),
      });
    } else {
      setState({
        ...state,
        break_start: 0,
        break_length: 0,
        break_start_2: 0,
        break_length_2: 0,
      });
      setBreakInfoString({
        break_start: get_t_str(0),
        break_end: get_t_str(0),
      });
      setBreakInfoString2({
        break_start: get_t_str(0),
        break_end: get_t_str(0),
      });
      set_break_checked_2(false);
    }
    setIsBreakManuallyControlled(true);
  }

  function handleSecondBreakCheckChange(e) {
    const { checked } = e.target;

    if (checked && !shiftQualifiesForSecondBreak(shiftLength)) {
      return;
    }

    set_break_checked_2(checked);

    if (checked) {
      const new_break_length = props.defaultBreakLengthHours;
      const shiftLength = state.shift_end - state.shift_start;
      const firstBreakEnd = state.break_start + state.break_length;
      const targetStart =
        Math.round((state.shift_start + shiftLength * 0.75 - new_break_length / 2) * 4) / 4;
      const new_break_start = Math.min(
        Math.max(targetStart, firstBreakEnd),
        state.shift_end - new_break_length
      );

      setState({
        ...state,
        break_start_2: new_break_start,
        break_length_2: new_break_length,
      });
      setBreakInfoString2({
        break_start: get_t_str(new_break_start),
        break_end: get_t_str(new_break_start + new_break_length),
      });
      return;
    }

    setState({
      ...state,
      break_start_2: 0,
      break_length_2: 0,
    });
    setBreakInfoString2({
      break_start: get_t_str(0),
      break_end: get_t_str(0),
    });
  }

  // Match new assigned shifts: derive rest breaks until the manager edits them.
  // Run after unpaid-break updates; functional writes preserve those updates.
  React.useEffect(() => {
    const regulation = props.restBreakRegulation;
    if (!props.open || props.existingShift || isPaidBreakManuallyControlled ||
        !props.paidBreakInputsEnabled || !regulation?.minimum_shift_hours ||
        !regulation?.trigger_hours) return;

    const shift = {
      ...state,
      break_length: break_checked ? state.break_length : 0,
      break_length_2: break_checked_2 ? state.break_length_2 : 0,
    };
    const workedHours = shift.shift_end - shift.shift_start -
      (shift.break_length || 0) - (shift.break_length_2 || 0);
    const count = requiredRestBreakCount(workedHours, regulation);
    const breaks = [];
    for (let index = 0; index < count; index++) {
      const start = getDefaultPaidBreakStart(shift, breaks, paidBreakDurationHours);
      if (start === null) break;
      breaks.push({ break_start: start, break_duration: paidBreakDurationHours });
    }
    breaks.sort((a, b) => a.break_start - b.break_start);
    setPaidBreaksChecked(breaks.length > 0);
    if (JSON.stringify(state.paid_breaks || []) !== JSON.stringify(breaks)) {
      setState((previous) => ({ ...previous, paid_breaks: breaks }));
    }
  }, [state, break_checked, break_checked_2, props.open, props.existingShift,
      props.paidBreakInputsEnabled, props.restBreakRegulation,
      isPaidBreakManuallyControlled, paidBreakDurationHours]);

  const initialNoteChecked = props.shift_info.note !== null;
  const [noteChecked, setNoteChecked] = React.useState(initialNoteChecked);

  function handleNoteCheckChange(e) {
    const { checked } = e.target;
    setNoteChecked(checked);
    if (!checked) {
      setState({
        ...state,
        note: null
      });
    }
  }

  function handleNoteChange(e) {
    const { value } = e.target;
    setState({
      ...state,
      note: value
    });
  }

  function handleBusinessDeclineCheckChange(e) {
    const { checked } = e.target;
    setState({
      ...state,
      business_decline: checked,
    });
  }



  function submitAndClose(shift_save_type) {
    if (shift_save_type !== "delete" && !paidBreakValidation.valid) return;
    const shift_info = buildOpenShiftSavePayload(
      state,
      break_checked,
      break_checked_2,
      selectedEmployee
    );

    props.addShift(shift_info, shift_save_type, appliedTo);
    if (shift_save_type === "delete") {
      setDeletingLoading(true);
    } else {
      setSavingLoading(true);
    }
  }

  const getRoleName = (role_id, role_lookups) => {
    const role_info = role_lookups.filter((r) => r.id === role_id);
    if (role_info.length > 0) {
      return role_info[0]["role_name"];
    } else {
      return null;
    }
  };

  const getRoleType = (role_id, role_lookups) => {
    const role_info = role_lookups.filter((r) => r.id === role_id);
    if (role_info.length > 0) {
      return role_info[0]["role_type"];
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (props.saveShiftFinished) {
      setSavingLoading(false);
      setDeletingLoading(false);
      props.handleClose();
    }
  }, [props.saveShiftFinished]);

  const day_abbrevs = createDayArray(props.businessWeekStartDayIndex);

  function generateBreakOptions(start, end) {
    const options = [];
    let currentTime = start;

    while (currentTime <= end) {
      options.push(get_t_str(currentTime));
      currentTime += 0.25;
    }

    return options;
  }

  function generateStationOptions(selectedRoleId) {
    const selectedRoleObject = props.role_names.find(
      (role) => role.id === selectedRoleId
    );
    return selectedRoleObject
      ? selectedRoleObject.station_options.map((station) => (
          <MenuItem key={station.station_id} value={station.station_id}>
            {station.station_name}
          </MenuItem>
        ))
      : [];
  }
  function generateStationDict(selectedRoleId, selectedStationId) {
    const selectedRoleObject = props.role_names.find(
      (role) => role.id === selectedRoleId
    );
    if (selectedRoleObject.station_options) {
      let station_dict = selectedRoleObject.station_options.find(
        (station) => station.station_id === selectedStationId
      );
      if (station_dict) {
        station_dict["id"] = station_dict["station_id"];
        station_dict["name"] = station_dict["station_name"];
        return station_dict;
      }
    }
    return null;
  }

  // console.log("ROLE NAMES INPUT", props.role_names);
  // console.log("STATE", state);

  const handleCloseNoSave = () => {
    setState(props.shift_info);
    setPaidBreaksChecked(Boolean(props.shift_info.paid_breaks?.length));
    setIsPaidBreakManuallyControlled(false);
    setShiftInfoString(initialShiftInfoString);
    setBreakInfoString(initialBreakInfoString);
    setBreakInfoString2(initialBreakInfoString2);
    set_break_checked(initialBreakChecked);
    set_break_checked_2(initialBreakChecked2);
    setIsBreakManuallyControlled(false);
    setNoteChecked(props.shift_info.note != null);
    setSelectedEmployeeId(null);

    let appliedToCopy = [false, false, false, false, false, false, false];
    appliedToCopy[props.d] = true;
    setAppliedTo(appliedToCopy);
    props.handleClose();
  };

  return (
    <div>
      <Drawer anchor="right" open={props.open} onClose={handleCloseNoSave}>
        <div className="w-[450px] h-full bg-slate-50">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader title="Open Shift" onClose={handleCloseNoSave} />
            <div className="w-full px-6 -mt-6 pb-24">
              <div>
                <p className="text-lg text-slate-500">{props.date_name}</p>
              </div>
              {!props.new_shift && props.existingShift && (
                <div className="mt-5 pb-5 border-b border-slate-200">
                  <h3 className="text-slate-900 font-bold text-sm">
                    Assignment
                  </h3>
                  <div className="mt-4">
                    <Autocomplete
                      id="open-shift-employee"
                      options={assignableEmployees}
                      value={selectedEmployee}
                      onChange={(event, employee) =>
                        setSelectedEmployeeId(employee?.id || null)
                      }
                      getOptionLabel={(employee) =>
                        `${employee?.first_name || ""} ${
                          employee?.last_name || ""
                        }`.trim()
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      noOptionsText="No employees can work this role"
                      renderOption={(optionProps, employee) => (
                        <li
                          {...optionProps}
                          className={`${optionProps.className || ""} flex items-center justify-between`}
                        >
                          <span>
                            {employee.first_name} {employee.last_name}
                          </span>
                          {pendingPickupUserIds.has(
                            normalizeOpenShiftUserId(
                              getOpenShiftEmployeeUserId(employee)
                            )
                          ) && (
                            <span className="ml-3 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                              Bid
                            </span>
                          )}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search employees"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "white",
                              borderRadius: "8px",
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#6d28d9",
                              },
                            },
                          }}
                        />
                      )}
                    />
                    <p className="mt-2 whitespace-nowrap text-xs text-slate-500">
                      Assigning moves this shift to the employee’s schedule.
                    </p>
                  </div>
                </div>
              )}
              <div className="mt-5 pb-5 border-b border-slate-200">
                <h3 className="text-slate-900 font-bold text-sm">Shift</h3>
                <div className="bg-white py-4 px-4 rounded-2xl mt-4 relative border border-slate-200 shadow-sm">
                  <div>
                    <p className="text-sm text-slate-500">Role</p>
                    <div className="mt-1">
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                        }}
                      >
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={state.role_id}
                          name="role_id"
                          onChange={handleChange}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "5px",
                            height: "45px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          }}
                        >
                          {props.role_names.length > 0 ? (
                            props.role_names
                              .sort((a, b) => {
                                if (a.role_type_id === b.role_type_id) {
                                  return a.role_name.localeCompare(b.role_name);
                                }
                                return a.role_type_id - b.role_type_id;
                              })
                              .map((role) => (
                                <MenuItem value={role.id}>
                                  {role.role_name}
                                </MenuItem>
                              ))
                          ) : (
                            <MenuItem value={null}>
                              {"No roles available"}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  {generateStationOptions(state.role_id)?.length > 0 &&
                    <div className="mt-3">
                      <Box sx={{ minWidth: 120 }}>
                        <p className="text-sm text-slate-500">Station</p>
                        <div className="w-full mt-1">
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{
                              backgroundColor: "white",
                              borderRadius: "8px",
                            }}
                          >
                            <Select
                              labelId="station-label"
                              placeholder="Choose a station"
                              id="station"
                              value={state.station_id}
                              name="station_id"
                              onChange={(e) => {
                                const new_station_id = e.target.value;
                                setState({
                                  ...state,
                                  station_id: new_station_id,
                                  station: generateStationDict(
                                    state.role_id,
                                    new_station_id
                                  ),
                                });
                              }}
                              sx={{
                                backgroundColor: "white",
                                borderRadius: "8px",
                                padding: "5px",
                                height: "45px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                              }}
                            >
                              {/* <MenuItem value={null}>NONE</MenuItem> */}
                              {generateStationOptions(state.role_id)}
                            </Select>
                          </FormControl>
                        </div>
                      </Box>
                    </div>
                  }
                  <div className="w-full flex gap-2 mt-3">
                    <div className="w-[132px]">
                      <p className="text-sm text-slate-500">Shift start</p>
                      <div className="mt-1">
                        <FormControl fullWidth size="small">
                          <Autocomplete
                            id="shift-start"
                            disableClearable
                            options={generateTimeOptions(
                              props.start_qhnum,
                              props.schedule_day_end_qhnum
                            )}
                            value={shiftInfoString.shift_start}
                            onChange={(event, newValue) => {
                              handleShiftInfo({
                                target: {
                                  name: "shift_start",
                                  value: newValue,
                                },
                              });
                            }}
                            filterOptions={filterOptions}
                            size="small"
                            sx={{
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#6d28d9",
                                },
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.key === "-" ||
                                event.key === "Enter" ||
                                event.key === "Tab"
                              ) {
                                event.preventDefault(); // Prevent the default dash behavior

                                // Select the first option from the autocomplete list
                                const firstOption = filterOptions(
                                  generateTimeOptions(
                                    props.start_qhnum,
                                    props.schedule_day_end_qhnum
                                  ),
                                  { inputValue: event.target.value }
                                )[0];

                                if (firstOption) {
                                  // Set the first option as the selected value
                                  handleShiftInfo({
                                    target: {
                                      name: "shift_start",
                                      value: firstOption,
                                    },
                                  });

                                  // Move focus directly to the Shift End input field by using its ID
                                  const shiftEndInput =
                                    document.getElementById("shift-end");
                                  if (shiftEndInput) {
                                    setTimeout(() => {
                                      shiftEndInput.focus(); // Move focus to the Shift End input field
                                      shiftEndInput.select();
                                    }, 0); // Adding a small timeout to ensure the DOM is ready
                                  }
                                }
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px !important",
                                    height: "45px",
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#6d28d9",
                                      },
                                    backgroundColor: "white",
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </div>
                    <div className="flex justify-center items-center text-slate-400 mt-6">
                      <ArrowForwardIcon />
                    </div>
                    <div className="w-[132px]">
                      <p className="text-sm text-slate-500">Shift end</p>
                      <div className="mt-1">
                        <FormControl fullWidth size="small">
                          <Autocomplete
                            id="shift-end"
                            disableClearable
                            options={generateTimeOptions(
                              props.start_qhnum,
                              props.schedule_day_end_qhnum
                            )}
                            value={shiftInfoString.shift_end}
                            onChange={(event, newValue) => {
                              handleShiftInfo({
                                target: { name: "shift_end", value: newValue },
                              });
                            }}
                            filterOptions={filterOptions}
                            size="small"
                            sx={{
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#6d28d9",
                                },
                            }}
                            // Handle key down event to select the first item and move focus to next field
                            onKeyDown={(event) => {
                              if (
                                (event.key === "Enter" ||
                                  event.key === "Tab") &&
                                event.target.value
                              ) {
                                event.preventDefault(); // Prevent the default tab/enter behavior
                                const firstOption = filterOptions(
                                  generateTimeOptions(
                                    props.start_qhnum,
                                    props.schedule_day_end_qhnum
                                  ),
                                  { inputValue: event.target.value }
                                )[0];
                                if (firstOption) {
                                  handleShiftInfo({
                                    target: {
                                      name: "shift_end",
                                      value: firstOption,
                                    },
                                  });

                                  // Move focus directly to the Shift End input field by using its ID
                                  const breakChecked =
                                    document.getElementById("break-checked");
                                  if (breakChecked) {
                                    setTimeout(() => {
                                      breakChecked.focus(); // Move focus to the Shift End input field
                                    }, 0); // Adding a small timeout to ensure the DOM is ready
                                  }
                                }
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px !important",
                                    height: "45px",
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#6d28d9",
                                      },
                                    backgroundColor: "white",
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </div>
                    <div className="w-16 flex flex-col justify-center items-center text-slate-400 text-xs mt-6">
                      <ScheduleIcon style={{ padding: "0px 5px" }} />{" "}
                      {state.shift_end - state.shift_start + " hrs"}
                    </div>
                  </div>
                  {showBusinessDeclineOption && (
                    <div
                      className={`mt-3 pb-3 ${
                        hasOptionAfterBusinessDecline
                          ? "border-b border-slate-200"
                          : ""
                      }`}
                    >
                      <div className="w-full flex items-center">
                        <Tooltip
                          title="Hides end time from employee. Managers still see the end time."
                          placement="top"
                          arrow
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="business-decline-checked"
                                name="businessDeclineChecked"
                                checked={!!state.business_decline}
                                onChange={handleBusinessDeclineCheckChange}
                                size="small"
                                sx={{
                                  color: "#6d28d9",
                                  "&.Mui-checked": {
                                    color: "#6d28d9",
                                  },
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleBusinessDeclineCheckChange({
                                      target: {
                                        name: "businessDeclineChecked",
                                        checked: !state.business_decline,
                                      },
                                    });
                                  }
                                }}
                              />
                            }
                          />
                        </Tooltip>
                        <p className="text-slate-900 text-sm -ml-3">Until Business Decline (BD)</p>
                      </div>
                    </div>
                  )}
                  <div
                    className={`py-3 ${
                      !showBusinessDeclineOption
                        ? "mt-3 border-t border-slate-200"
                        : ""
                    } ${
                      hasOptionAfterBreak ? "border-b border-slate-200" : ""
                    }`}
                  >
                    <div className="flex items-center">
                      <FormControlLabel
                        sx={OPTION_SWITCH_LABEL_SX}
                        control={
                          <Switch
                            id="break-checked"
                            name="break_checked"
                            checked={break_checked}
                            onChange={handleCheckChange}
                            size="small"
                            sx={OPTION_SWITCH_SX}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault(); // Prevent default Enter behavior
                                handleCheckChange({
                                  target: {
                                    name: "break_checked",
                                    checked: !break_checked,
                                  },
                                });
                              }
                            }}
                          />
                        }
                      />
                      <p className="text-slate-900 text-sm ml-1">{props.paidBreakInputsEnabled ? "Add unpaid break" : "Add break"}</p>
                    </div>
                    {break_checked ? (
                      <div>
                        <div className="w-full flex mt-1 gap-2 pb-2">
                          <div className="w-[132px]">
                          {/* <TextField
                            size="small"
                            value={breakInfoString.break_start}
                            name="break_start"
                            onChange={handleBreakInfo}
                          >
                            {breakInfoString.break_start}
                          </TextField> */}
                          <p className="text-sm text-slate-500">Break start</p>
                          <div className="mt-1">
                            <FormControl fullWidth size="small">
                              <Autocomplete
                                id="break_start"
                                name="break_start"
                                disableClearable
                                options={generateBreakOptions(
                                  text_to_num(shiftInfoString.shift_start),
                                  text_to_num(shiftInfoString.shift_end)
                                )}
                                value={breakInfoString.break_start}
                                onChange={(event, newValue) => {
                                  handleBreakInfo({
                                    target: {
                                      name: "break_start",
                                      value: newValue,
                                    },
                                  });
                                }}
                                filterOptions={filterOptions}
                                size="small"
                                sx={{
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                }}
                                // Handle key down event to select the first item and move focus to next field
                                onKeyDown={(event) => {
                                  if (
                                    (event.key === "Enter" ||
                                      event.key === "-" ||
                                      event.key === "Tab") &&
                                    event.target.value
                                  ) {
                                    event.preventDefault(); // Prevent the default tab/enter behavior
                                    const firstOption = filterOptions(
                                      generateBreakOptions(
                                        text_to_num(
                                          shiftInfoString.shift_start
                                        ),
                                        text_to_num(shiftInfoString.shift_end)
                                      ),
                                      { inputValue: event.target.value }
                                    )[0];
                                    if (firstOption) {
                                      handleBreakInfo({
                                        target: {
                                          name: "break_start",
                                          value: firstOption,
                                        },
                                      });

                                      // Move focus directly to the Shift End input field by using its ID
                                      const breakEnd =
                                        document.getElementById("break-end");
                                      if (breakEnd) {
                                        setTimeout(() => {
                                          breakEnd.focus(); // Move focus to the Shift End input field
                                          breakEnd.select(); // Select the current text so it's highlighted and ready to be overwritten
                                        }, 0); // Adding a small timeout to ensure the DOM is ready
                                      }
                                    }
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px !important",
                                        height: "45px",
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#6d28d9",
                                          },
                                        backgroundColor: "white",
                                      },
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </div>
                        </div>
                          <div className="flex justify-center items-center text-slate-400 mt-6">
                            <ArrowForwardIcon />
                          </div>
                          <div className="w-[132px]">
                          <p className="text-sm text-slate-500">Break end</p>
                          <div className="mt-1">
                            <FormControl fullWidth size="small">
                              <Autocomplete
                                id="break-end"
                                name="break_end"
                                disableClearable
                                options={generateBreakOptions(
                                  text_to_num(shiftInfoString.shift_start),
                                  text_to_num(shiftInfoString.shift_end)
                                )}
                                value={breakInfoString.break_end}
                                onChange={(event, newValue) => {
                                  handleBreakInfo({
                                    target: {
                                      name: "break_end",
                                      value: newValue,
                                    },
                                  });
                                }}
                                filterOptions={filterOptions}
                                size="small"
                                sx={{
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                }}
                                // Handle key down event to select the first item and move focus to next field
                                onKeyDown={(event) => {
                                  if (
                                    (event.key === "Enter" ||
                                      event.key === "Tab") &&
                                    event.target.value
                                  ) {
                                    event.preventDefault(); // Prevent the default tab/enter behavior
                                    const firstOption = filterOptions(
                                      generateBreakOptions(
                                        text_to_num(
                                          shiftInfoString.shift_start
                                        ),
                                        text_to_num(shiftInfoString.shift_end)
                                      ),
                                      { inputValue: event.target.value }
                                    )[0];
                                    if (firstOption) {
                                      handleBreakInfo({
                                        target: {
                                          name: "break_end",
                                          value: firstOption,
                                        },
                                      });
                                      // Move focus directly to the Shift End input field by using its ID
                                      const saveShift =
                                        document.getElementById("save-shift");
                                      if (saveShift) {
                                        setTimeout(() => {
                                          saveShift.focus(); // Move focus to the Shift End input field
                                        }, 0); // Adding a small timeout to ensure the DOM is ready
                                      }
                                    }
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px !important",
                                        height: "45px",
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#6d28d9",
                                          },
                                        backgroundColor: "white",
                                      },
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </div>
                        </div>
                          <div className="w-16 flex flex-col justify-center items-center text-slate-400 text-xs mt-6">
                            <ScheduleIcon style={{ padding: "0px 5px" }} />{" "}
                            {state.break_length + " hrs"}
                          </div>
                        </div>
                        {showSecondBreakOption && (
                          <div className="mt-2">
                            <div className="flex items-center">
                              <FormControlLabel
                                sx={OPTION_SWITCH_LABEL_SX}
                                control={
                                  <Switch
                                    id="break-checked-2"
                                    name="break_checked_2"
                                    checked={break_checked_2}
                                    onChange={handleSecondBreakCheckChange}
                                    size="small"
                                    sx={OPTION_SWITCH_SX}
                                  />
                                }
                              />
                              <p className="text-slate-900 text-sm ml-1">
                                Add second unpaid break
                              </p>
                            </div>
                            {break_checked_2 && (
                              <div className="w-full flex mt-1 gap-2 pb-2">
                              <div className="w-[132px]">
                                <p className="text-sm text-slate-500">2nd Break start</p>
                                <div className="mt-1">
                                  <FormControl fullWidth size="small">
                                    <Autocomplete
                                      id="break_start_2"
                                      name="break_start_2"
                                      disableClearable
                                      options={generateBreakOptions(
                                        text_to_num(shiftInfoString.shift_start),
                                        text_to_num(shiftInfoString.shift_end)
                                      )}
                                      value={breakInfoString2.break_start}
                                      onChange={(event, newValue) => {
                                        handleBreakInfo({
                                          target: {
                                            name: "break_start_2",
                                            value: newValue,
                                          },
                                        });
                                      }}
                                      filterOptions={filterOptions}
                                      size="small"
                                      sx={{
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#6d28d9",
                                          },
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "8px !important",
                                              height: "45px",
                                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                {
                                                  borderColor: "#6d28d9",
                                                },
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      )}
                                    />
                                  </FormControl>
                                </div>
                              </div>
                              <div className="flex justify-center items-center text-slate-400 mt-6">
                                <ArrowForwardIcon />
                              </div>
                              <div className="w-[132px]">
                                <p className="text-sm text-slate-500">2nd Break end</p>
                                <div className="mt-1">
                                  <FormControl fullWidth size="small">
                                    <Autocomplete
                                      id="break_end_2"
                                      name="break_end_2"
                                      disableClearable
                                      options={generateBreakOptions(
                                        text_to_num(shiftInfoString.shift_start),
                                        text_to_num(shiftInfoString.shift_end)
                                      )}
                                      value={breakInfoString2.break_end}
                                      onChange={(event, newValue) => {
                                        handleBreakInfo({
                                          target: {
                                            name: "break_end_2",
                                            value: newValue,
                                          },
                                        });
                                      }}
                                      filterOptions={filterOptions}
                                      size="small"
                                      sx={{
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: "#6d28d9",
                                          },
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: "8px !important",
                                              height: "45px",
                                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                {
                                                  borderColor: "#6d28d9",
                                                },
                                              backgroundColor: "white",
                                            },
                                          }}
                                        />
                                      )}
                                    />
                                  </FormControl>
                                </div>
                              </div>
                              <div className="w-16 flex flex-col justify-center items-center text-slate-400 text-xs mt-6">
                                <ScheduleIcon style={{ padding: "0px 5px" }} />{" "}
                                {(state.break_length_2 || 0) + " hrs"}
                              </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div />
                    )}
                  </div>
                  {props.paidBreakInputsEnabled && (
                    <PaidBreakInputs
                      paidBreakDurationHours={paidBreakDurationHours}
                      paidBreaksChecked={paidBreaksChecked}
                      paidBreaks={paidBreaks}
                      canAddPaidBreak={canAddPaidBreak}
                      handlePaidBreaksCheckChange={handlePaidBreaksCheckChange}
                      handleAddPaidBreak={handleAddPaidBreak}
                      handlePaidBreakChange={handlePaidBreakChange}
                      handleRemovePaidBreak={handleRemovePaidBreak}
                      paidBreakValidation={paidBreakValidation}
                      startQhnum={props.start_qhnum}
                      scheduleDayEndQhnum={props.schedule_day_end_qhnum}
                    />
                  )}
                  <div className="py-3">
                    <div className="w-full flex items-center">
                      <FormControlLabel
                        sx={OPTION_SWITCH_LABEL_SX}
                        control={
                          <Switch
                            id="note-checked"
                            name="noteChecked"
                            checked={noteChecked}
                            onChange={handleNoteCheckChange}
                            size="small"
                            sx={OPTION_SWITCH_SX}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault(); // Prevent default Enter behavior
                                handleNoteCheckChange({
                                  target: { name: "noteChecked", checked: !noteChecked },
                                });
                              }
                            }}
                          />
                        }
                      />
                      <p className="text-slate-900 text-sm ml-1">Add note</p>
                    </div>
                    {noteChecked && (
                      <div className="w-full pb-2">
                        <div className="w-full">
                          <p className="text-sm text-slate-500">Note</p>
                          <div className="mt-1">
                            <TextField
                              placeholder="Add a note to open shift card"
                              fullWidth
                              variant="outlined"
                              size="small"
                              name="note"
                              value={state.note || ''} // Add fallback empty string
                              onChange={handleNoteChange}
                              InputProps={{
                                sx: {
                                  backgroundColor: "white",
                                  borderRadius: "8px",
                                  padding: "5px",
                                  height: "45px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#6d28d9",
                                  },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-5 pb-5 border-slate-200">
                <h3 className="text-sm text-slate-900 font-bold">Apply To</h3>
                <div className="mt-4">{appliedToButtonGroup()}</div>
              </div>

            </div>
            <div className="w-full h-[40px]"></div>
            {props.new_shift ? (
              <DrawerFooterContainerSmall>
                <LoadingButton
                  id="save-shift"
                  widthFull
                  variant="contained"
                  loading={savingLoading}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  disabled={!checkIfValid(state, break_checked) || !paidBreakValidation.valid || state.role_id == null}
                  onClick={() => submitAndClose("add")}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "16px",
                  }}
                >
                  Create
                </LoadingButton>
              </DrawerFooterContainerSmall>
            ) : (
              <DrawerFooterContainerSmall>
                <div className="w-full h-full flex items-center">
                  <div className="w-1/2 pr-2">
                    <LoadingButton
                      widthFull
                      variant="contained"
                      loading={deletingLoading}
                      loadingPosition="end"
                      endIcon={<DeleteIcon />}
                      onClick={() => submitAndClose("delete")}
                      sx={{
                        width: "100%",
                        textTransform: "none",
                        borderRadius: "12px",
                        paddingY: "14px",
                        backgroundColor: "#e11d48",
                        "&:hover": {
                          backgroundColor: "#be123c",
                        },
                        fontSize: "16px",
                      }}
                    >
                      Delete
                    </LoadingButton>
                  </div>
                  <div className="w-1/2 pl-2">
                    <LoadingButton
                      id="save-shift"
                      widthFull
                      variant="contained"
                      loading={savingLoading}
                      loadingPosition="end"
                      endIcon={<SaveIcon />}
                      disabled={!checkIfValid(state, break_checked) || !paidBreakValidation.valid || state.role_id == null}
                      onClick={() => submitAndClose("edit")}
                      sx={{
                        width: "100%",
                        textTransform: "none",
                        borderRadius: "12px",
                        paddingY: "14px",
                        backgroundColor: "#6d28d9",
                        "&:hover": {
                          backgroundColor: "#5b21b6",
                        },
                        fontSize: "16px",
                      }}
                    >
                      {selectedEmployee ? "Assign shift" : "Save"}
                    </LoadingButton>
                  </div>
                </div>
              </DrawerFooterContainerSmall>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default OpenShiftInputsModal;
